import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  HiOutlineOfficeBuilding, 
  HiOutlinePencil, 
  HiOutlineTrash, 
  HiOutlineGlobeAlt,
  HiOutlineLocationMarker,
  HiOutlineStar,
  HiOutlineBriefcase,
  HiOutlineUsers,
  HiOutlineCalendar,
  HiOutlinePlus,
  HiOutlineEye,
  HiOutlineArrowLeft,
  HiOutlineLink
} from 'react-icons/hi';
import { Company, Application, Contact, UpdateCompanyData } from '../types';
import { companiesService } from '../services/companiesService';
import { applicationsService } from '../services/applicationsService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const CompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [company, setCompany] = useState<Company | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const [editForm, setEditForm] = useState<UpdateCompanyData>({
    name: '',
    description: '',
    website: '',
    linkedin: '',
    location: '',
    size: 'medium',
    notes: '',
    rating: undefined,
    tags: []
  });

  const [newTag, setNewTag] = useState('');

  const fetchCompanyData = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [companyRes, applicationsRes, contactsRes] = await Promise.all([
        companiesService.getCompany(id),
        applicationsService.getApplicationsByCompany(id, { limit: 100 }),
        companiesService.getCompanyContacts(id, { limit: 100 })
      ]);

      setCompany(companyRes);
      setApplications(applicationsRes.data);
      setContacts(contactsRes.data);
      
      // Initialize edit form
      setEditForm({
        name: companyRes.name || '',
        description: companyRes.description || '',
        website: companyRes.website || '',
        linkedin: companyRes.linkedin || '',
        location: companyRes.location || '',
        size: companyRes.size || 'medium',
        notes: companyRes.notes || '',
        rating: companyRes.rating,
        tags: companyRes.tags || []
      });
    } catch (error) {
      console.error('Error fetching company data:', error);
      toast.error('Failed to load company data');
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      fetchCompanyData();
    }
  }, [id, fetchCompanyData]);

  const handleEdit = async () => {
    if (!id || !company) return;

    try {
      const updatedCompany = await companiesService.updateCompany(id, editForm);
      setCompany(updatedCompany);
      setEditMode(false);
      toast.success('Company updated successfully');
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Failed to update company');
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      await companiesService.deleteCompany(id);
      toast.success('Company deleted successfully');
      navigate('/companies');
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Failed to delete company');
    }
  };

  const addTag = () => {
    if (newTag.trim() && !editForm.tags?.includes(newTag.trim())) {
      setEditForm({
        ...editForm,
        tags: [...(editForm.tags || []), newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setEditForm({
      ...editForm,
      tags: editForm.tags?.filter(tag => tag !== tagToRemove) || []
    });
  };

  const getSizeLabel = (size: string) => {
    const sizeLabels = {
      startup: 'Startup (1-10)',
      small: 'Small (11-50)',
      medium: 'Medium (51-200)',
      large: 'Large (201-1000)',
      enterprise: 'Enterprise (1000+)'
    };
    return sizeLabels[size as keyof typeof sizeLabels] || size;
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <HiOutlineStar
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    const colors = {
      applied: 'bg-blue-100 text-blue-800',
      interview: 'bg-yellow-100 text-yellow-800',
      offer: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      withdrawn: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <HiOutlineOfficeBuilding className="mx-auto h-12 w-12 text-secondary-400" />
        <h3 className="mt-2 text-sm font-medium text-secondary-900">Company not found</h3>
        <p className="mt-1 text-sm text-secondary-500">
          The company you're looking for doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate('/companies')}
          className="mt-4 btn btn-primary"
        >
          Back to Companies
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/companies')}
            className="p-2 text-secondary-400 hover:text-secondary-600 rounded-full hover:bg-secondary-100"
          >
            <HiOutlineArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">{company.name}</h1>
            <p className="mt-1 text-sm text-secondary-600">
              Company Details and Analytics
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          {!editMode ? (
            <>
              <button
                onClick={() => setEditMode(true)}
                className="btn btn-secondary"
              >
                <HiOutlinePencil className="h-4 w-4 mr-2" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="btn bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
              >
                <HiOutlineTrash className="h-4 w-4 mr-2" />
                Delete
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleEdit}
                className="btn btn-primary"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  setEditForm({
                    name: company.name || '',
                    description: company.description || '',
                    website: company.website || '',
                    linkedin: company.linkedin || '',
                    location: company.location || '',
                    size: company.size || 'medium',
                    notes: company.notes || '',
                    rating: company.rating,
                    tags: company.tags || []
                  });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-content">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <HiOutlineBriefcase className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-secondary-500 truncate">
                    Applications
                  </dt>
                  <dd className="text-lg font-medium text-secondary-900">
                    {company.applicationCount || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <HiOutlineUsers className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-secondary-500 truncate">
                    Contacts
                  </dt>
                  <dd className="text-lg font-medium text-secondary-900">
                    {contacts.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <HiOutlineCalendar className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-secondary-500 truncate">
                    Last Interaction
                  </dt>
                  <dd className="text-lg font-medium text-secondary-900">
                    {formatDate(company.lastInteraction)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <HiOutlineStar className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-secondary-500 truncate">
                    Rating
                  </dt>
                  <dd className="text-lg font-medium text-secondary-900">
                    {company.rating ? (
                      <div className="flex items-center">
                        {renderStars(company.rating)}
                        <span className="ml-1">({company.rating}/5)</span>
                      </div>
                    ) : (
                      'Not rated'
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Company Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Company Information</h3>
        </div>
        <div className="card-content space-y-6">
          {editMode ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Company Size
                </label>
                <select
                  value={editForm.size}
                  onChange={(e) => setEditForm({ ...editForm, size: e.target.value as any })}
                  className="input w-full"
                >
                  <option value="startup">Startup (1-10)</option>
                  <option value="small">Small (11-50)</option>
                  <option value="medium">Medium (51-200)</option>
                  <option value="large">Large (201-1000)</option>
                  <option value="enterprise">Enterprise (1000+)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={editForm.website}
                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                  className="input w-full"
                  placeholder="https://company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  LinkedIn
                </label>
                <input
                  type="url"
                  value={editForm.linkedin}
                  onChange={(e) => setEditForm({ ...editForm, linkedin: e.target.value })}
                  className="input w-full"
                  placeholder="https://linkedin.com/company/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  className="input w-full"
                  placeholder="City, State, Country"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Rating
                </label>
                <select
                  value={editForm.rating || ''}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    rating: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                  className="input w-full"
                >
                  <option value="">No rating</option>
                  <option value="1">1 Star</option>
                  <option value="2">2 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="5">5 Stars</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="input w-full"
                  placeholder="Company description..."
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="input w-full"
                  placeholder="Private notes about this company..."
                />
              </div>

              {/* Tags Management */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Tags
                </label>
                <div className="flex space-x-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    className="input flex-1"
                    placeholder="Add a tag..."
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="btn btn-secondary"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editForm.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 h-4 w-4 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-4">
                {company.description && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-500">Description</h4>
                    <p className="mt-1 text-sm text-secondary-900">{company.description}</p>
                  </div>
                )}

                {company.location && (
                  <div className="flex items-center text-sm text-secondary-600">
                    <HiOutlineLocationMarker className="h-4 w-4 mr-2" />
                    {company.location}
                  </div>
                )}

                <div className="flex items-center text-sm text-secondary-600">
                  <HiOutlineOfficeBuilding className="h-4 w-4 mr-2" />
                  {getSizeLabel(company.size || 'medium')}
                </div>
              </div>

              <div className="space-y-4">
                {company.website && (
                  <div>
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <HiOutlineGlobeAlt className="h-4 w-4 mr-2" />
                      Website
                    </a>
                  </div>
                )}

                {company.linkedin && (
                  <div>
                    <a
                      href={company.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <HiOutlineLink className="h-4 w-4 mr-2" />
                      LinkedIn
                    </a>
                  </div>
                )}

                {company.tags && company.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-500 mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {company.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {company.notes && (
                <div className="sm:col-span-2">
                  <h4 className="text-sm font-medium text-secondary-500">Notes</h4>
                  <p className="mt-1 text-sm text-secondary-900 whitespace-pre-wrap">{company.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recent Applications */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-medium text-secondary-900">Recent Applications</h3>
          <button
            onClick={() => navigate('/applications')}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View all
          </button>
        </div>
        <div className="card-content">
          {applications.length > 0 ? (
            <div className="space-y-4">
              {applications.slice(0, 5).map((application) => (
                <div
                  key={application._id}
                  className="flex items-center justify-between py-3 border-b border-secondary-200 last:border-b-0"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <h4 className="text-sm font-medium text-secondary-900">
                        {application.role}
                      </h4>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}
                      >
                        {application.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-secondary-500">
                        Applied {formatDate(application.appliedDate)}
                      </span>
                    </div>
                    {application.notes && (
                      <p className="mt-1 text-sm text-secondary-600 line-clamp-1">
                        {application.notes}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/applications/${application._id}`)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <HiOutlineEye className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <HiOutlineBriefcase className="mx-auto h-8 w-8 text-secondary-400" />
              <p className="mt-2 text-sm text-secondary-500">No applications yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Company Contacts */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-medium text-secondary-900">Company Contacts</h3>
        </div>
        <div className="card-content">
          {contacts.length > 0 ? (
            <div className="space-y-4">
              {contacts.slice(0, 5).map((contact) => (
                <div
                  key={contact._id}
                  className="flex items-center justify-between py-3 border-b border-secondary-200 last:border-b-0 hover:bg-secondary-100 hover:shadow-sm transition-all duration-200 cursor-pointer rounded-md px-2"
                  onClick={() => navigate(`/contacts/${contact._id}`)}
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-secondary-900">{contact.name}</span>
                      {contact.title && (
                        <span className="text-sm text-secondary-500">{contact.title}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-secondary-500">
                      {contact.email && <span>{contact.email}</span>}
                      {contact.relationship && (
                        <span className="capitalize">{contact.relationship}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <HiOutlineUsers className="mx-auto h-8 w-8 text-secondary-400" />
              <p className="mt-2 text-sm text-secondary-500">No contacts yet</p>
              <button
                onClick={() => navigate('/contacts/new', { state: { companyId: id } })}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Add the first contact
              </button>
            </div>
          )}
        </div>
        <div className="card-footer flex justify-end space-x-2">
          <button
            onClick={() => navigate(`/contacts?company=${id}`)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View all
          </button>
          <button
            onClick={() => navigate('/contacts/new', { state: { companyId: id } })}
            className="btn btn-sm btn-primary"
          >
            <HiOutlinePlus className="h-4 w-4 mr-1" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Delete Company
            </h3>
            <p className="text-sm text-secondary-600 mb-6">
              Are you sure you want to delete "{company.name}"? This action cannot be undone and will also delete all related applications and contacts.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn bg-red-600 text-white hover:bg-red-700"
              >
                Delete Company
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetail; 